import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ONBOARDING } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Clock, Globe, Mail, ShieldCheck } from "lucide-react";

const COMMON_TZS = [
  "America/Los_Angeles","America/Denver","America/Chicago","America/New_York",
  "America/Phoenix","America/Anchorage","Pacific/Honolulu","Europe/London",
  "Europe/Paris","Europe/Berlin","Europe/Madrid","Asia/Kolkata","Asia/Singapore",
  "Asia/Tokyo","Australia/Sydney","Australia/Melbourne","UTC",
];

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const detected = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC", []);
  const [time, setTime] = useState("18:00");
  const [tz, setTz] = useState(detected);
  const [busy, setBusy] = useState(false);

  const tzOptions = useMemo(() => {
    const set = new Set(COMMON_TZS);
    set.add(detected);
    return Array.from(set).sort();
  }, [detected]);

  const submit = async () => {
    const [h, m] = time.split(":").map((x) => parseInt(x, 10));
    if (Number.isNaN(h) || Number.isNaN(m)) {
      toast.error("Pick a valid time");
      return;
    }
    setBusy(true);
    try {
      await api.post("/settings/schedule", { hour: h, minute: m, tz });
      toast.success("You're all set. Your first scan will run at the scheduled time.");
      // Check for a pending Wix deep-link checkout intent.
      let pending = null;
      try { pending = localStorage.getItem("ca:auto-checkout"); } catch { /* ignore */ }
      // Decide destination BEFORE calling refresh() — otherwise the user.onboarded
      // flip inside refresh() lets OnboardingGate race-redirect to /dashboard and
      // stomp our nav.
      if (pending === "monthly" || pending === "annual") {
        try { localStorage.removeItem("ca:auto-checkout"); } catch { /* ignore */ }
        nav(`/pricing?plan=${pending}&auto=1`, { replace: true });
      } else if (user && !user.demo_mode && !user.billing?.active) {
        // Non-demo, non-tester users without an active subscription go to Pricing next.
        nav("/pricing", { replace: true });
      } else {
        nav("/dashboard", { replace: true });
      }
      // Refresh AFTER navigation so setUser(user.onboarded=true) can't trigger
      // OnboardingGate to override our destination.
      await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not save schedule";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-8 w-8 rounded-md bg-[#971a8c] text-white flex items-center justify-center">
            <Mail className="h-4 w-4" />
          </div>
          <span className="font-heading text-lg text-[#1C2321]">ContactAssist</span>
        </div>

        <div className="mb-8" data-testid={ONBOARDING.stepIndicator}>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8B9891] mb-3">Step 2 of 2 — schedule</p>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-[#1C2321] font-medium mb-3">
            Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
          </h1>
          <p className="text-base leading-relaxed text-[#5D6D63] max-w-lg">
            Pick a daily time <em>outside school hours</em>. ContactAssist will scan the last 24 hours of your inbox, extract parent messages, and append them to your Google Sheet.
          </p>
        </div>

        {/* Permissions recap — reminds the teacher what they just granted */}
        <div
          data-testid="onboarding-permissions-recap"
          className="mb-6 rounded-lg border border-[#E8E6DF] bg-[#F9F8F6] p-5"
        >
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="h-4 w-4 text-[#971a8c]" />
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8B9891]">
              What you just granted
            </span>
          </div>
          <ul className="text-sm text-[#5D6D63] space-y-1.5 list-disc pl-5">
            <li>Read your Gmail so we can find parent messages.</li>
            <li>
              Save AI-drafted replies as <strong className="text-[#1C2321]">drafts</strong> in your Gmail.{" "}
              <strong className="text-[#1C2321]">Nothing is sent unless you click Send yourself.</strong>
            </li>
            <li>Send your opt-in weekly digest from your own Gmail (only if you turn it on in Settings).</li>
            <li>Create and edit one Google Sheet in your Drive — nothing else.</li>
          </ul>
          <p className="text-xs text-[#8B9891] mt-3">
            You can revoke access anytime at{" "}
            <a
              href="https://myaccount.google.com/permissions"
              target="_blank"
              rel="noreferrer"
              className="underline hover:text-[#971a8c]"
            >
              myaccount.google.com/permissions
            </a>
            .
          </p>
        </div>

        <div className="bg-white border border-[#E8E6DF] rounded-lg p-6 sm:p-8">
          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8B9891] flex items-center gap-2 mb-2">
                <Clock className="h-3 w-3" /> Daily trigger time
              </label>
              <Input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid={ONBOARDING.timeInput}
                className="h-11 rounded-md border-[#E8E6DF] focus-visible:ring-1 focus-visible:ring-[#971a8c]"
              />
              <p className="mt-2 text-xs text-[#8B9891]">Suggested: 6:00 PM or later.</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8B9891] flex items-center gap-2 mb-2">
                <Globe className="h-3 w-3" /> Timezone (detected)
              </label>
              <Select value={tz} onValueChange={setTz}>
                <SelectTrigger className="h-11 rounded-md border-[#E8E6DF]" data-testid={ONBOARDING.tzSelect}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tzOptions.map((z) => (
                    <SelectItem key={z} value={z}>{z}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-2 text-xs text-[#8B9891]">Auto-detected from your browser.</p>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-[#E8E6DF] flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-[#5D6D63]">
              <CheckCircle2 className="h-4 w-4 text-[#971a8c]" />
              Google connected as <strong className="text-[#1C2321]">{user?.email}</strong>
            </div>
            <Button
              data-testid={ONBOARDING.submitBtn}
              onClick={submit}
              disabled={busy}
              className="h-11 rounded-md px-6 bg-[#971a8c] text-white hover:bg-[#7d1574] transition-colors"
            >
              {busy ? "Saving…" : "Save & finish"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
